/**
 * Extract a representative frame from a video file as a PNG Blob.
 * Pure browser APIs — no ffmpeg, no server processing.
 *
 * Strategy: load the video into a hidden <video>, seek to ~25% duration
 * (skip intros/black frames), draw to canvas, export PNG.
 */
export interface ExtractFrameOptions {
  /** Fraction of duration to seek to (0..1). Default 0.25. */
  seekFraction?: number;
  /** Max long-edge pixel size for the output frame. Default 1280. */
  maxEdge?: number;
  /** Output mime type. Default "image/jpeg" (smaller than PNG). */
  mime?: "image/jpeg" | "image/png";
  /** JPEG quality 0..1. Default 0.9. */
  quality?: number;
}

export async function extractVideoFrame(
  file: File,
  opts: ExtractFrameOptions = {},
): Promise<Blob> {
  const { seekFraction = 0.25, maxEdge = 1280, mime = "image/jpeg", quality = 0.9 } = opts;

  const url = URL.createObjectURL(file);
  const video = document.createElement("video");
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;
  // Some Chromium contexts reject blob: URLs in <video> with a "URL safety
  // check" error unless the element is attached to the DOM. Mount it hidden.
  video.style.cssText =
    "position:fixed;left:-99999px;top:-99999px;width:1px;height:1px;opacity:0;pointer-events:none;";
  document.body.appendChild(video);
  video.src = url;

  try {
    // Wait until metadata is loaded AND dimensions are available. Some
    // browsers fire loadedmetadata with videoWidth/Height still 0; we then
    // wait for loadeddata for the first frame to be decoded.
    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        video.removeEventListener("loadedmetadata", onMeta);
        video.removeEventListener("loadeddata", onData);
        video.removeEventListener("error", onErr);
      };
      const onMeta = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          cleanup();
          resolve();
        }
      };
      const onData = () => {
        cleanup();
        resolve();
      };
      const onErr = () => {
        cleanup();
        reject(new Error("video_metadata_load_failed"));
      };
      video.addEventListener("loadedmetadata", onMeta);
      video.addEventListener("loadeddata", onData);
      video.addEventListener("error", onErr);
    });

    const duration = isFinite(video.duration) ? video.duration : 0;
    const target = Math.max(0.1, Math.min(duration * seekFraction, Math.max(duration - 0.1, 0)));

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("error", onErr);
      };
      const onSeeked = () => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve();
      };
      const onErr = () => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error("video_seek_failed"));
      };
      video.addEventListener("seeked", onSeeked);
      video.addEventListener("error", onErr);
      try {
        video.currentTime = target;
      } catch (err) {
        if (!settled) {
          settled = true;
          cleanup();
          reject(err instanceof Error ? err : new Error(String(err)));
        }
      }
      // Hard timeout: some browsers fail to fire seeked silently. Fall back
      // to drawing whatever frame is currently buffered.
      setTimeout(() => {
        if (!settled) {
          settled = true;
          cleanup();
          resolve();
        }
      }, 5000);
    });

    // If readyState is still < HAVE_CURRENT_DATA, give the decoder a beat.
    if (video.readyState < 2) {
      await new Promise<void>((resolve) => {
        const onReady = () => {
          video.removeEventListener("loadeddata", onReady);
          resolve();
        };
        video.addEventListener("loadeddata", onReady);
        setTimeout(() => {
          video.removeEventListener("loadeddata", onReady);
          resolve();
        }, 3000);
      });
    }

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) throw new Error("video_dimensions_unavailable");

    const scale = Math.min(1, maxEdge / Math.max(vw, vh));
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas_context_unavailable");
    ctx.drawImage(video, 0, 0, w, h);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("canvas_to_blob_failed"));
            return;
          }
          resolve(blob);
        },
        mime,
        quality,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
    video.src = "";
    try {
      video.load();
    } catch {
      // ignore
    }
    if (video.parentNode) video.parentNode.removeChild(video);
  }
}

/**
 * Resize an arbitrary image File to a max long-edge dimension and re-encode
 * as JPEG. Keeps payload small and consistent for vision API.
 */
export async function compressImage(
  file: File,
  maxEdge = 1280,
  quality = 0.9,
): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    await img.decode();
    const scale = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas_context_unavailable");
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("canvas_to_blob_failed"))),
        "image/jpeg",
        quality,
      );
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
